import { useEffect, useState } from "react";
import "./megamenu.css";
import {
  Box,
  Card,
  CardContent,
  Link,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useLocation, Link as RouterLink } from "react-router-dom";

interface linkProps {
  label: string;
  to: string;
}

interface subMenuProps {
  label: string;
  links: linkProps[];
}

interface menuProps {
  value: string;
  childrens: subMenuProps[];
}

const menuDatas: menuProps[] = [
  {
    value: "Forward Kinematics",
    childrens: [
      {
        label: "Fundamental",
        links: [
          { label: "Translation", to: "/robotics/translation" },
          { label: "Rotation", to: "/robotics/rotation" },
          { label: "Euler Angles", to: "/robotics/eulerangles" },
          { label: "Quaternion", to: "/robotics/quaternion" },
          {
            label: "Transformation\n (Translation+Rotation)",
            to: "/robotics/homogeneous-transformations",
          },
        ],
      },
      {
        label: "DH Parameters",
        links: [
          { label: "Classic DH Parameters", to: "/robotics/dh-model" },
          {
            label: "Modified DH Parameters",
            to: "/robotics/modified-dh-model",
          },
        ],
      },
    ],
  },
  {
    value: "Inverse Kinematics",
    childrens: [
      {
        label: "Analytical Solutions",
        links: [{ label: "Inverse Kinematics", to: "/robotics/ik" }],
      },
      {
        label: "Numerical Solutions",
        links: [
          {
            label: "2D Jacobian Inverse Kinematics",
            to: "/robotics/2d-jacobian-ik",
          },
        ],
      },
    ],
  },
  {
    value: "Dynamics",
    childrens: [
      {
        label: "Lagrange",
        links: [
          { label: "Lagrange Dynamics Equation", to: "/robotics/lagrange" },
        ],
      },
    ],
  },
  {
    value: "Control Theory",
    childrens: [
      {
        label: "PID",
        links: [{ label: "PID Control with Cart-Pole", to: "/robotics/pid" }],
      },
    ],
  },
  {
    value: "Motion Planning",
    childrens: [
      {
        label: "Path Planning with Voronoi Diagram",
        links: [
          { label: "Voronoi Diagram", to: "/robotics/voronoi" },
          {
            label: "Generalized Voronoi Diagram",
            to: "/robotics/generalized-voronoi-diagram",
          },
          {
            label: "Path Planning with Voronoi Diagram",
            to: "/robotics/path-planning-with-voronoi",
          },
        ],
      },
      {
        label: "Path Planning with Cell/Grid Decomposition",
        links: [
          { label: "Cell Decomposition", to: "/robotics/cell-decomposition" },
        ],
      },
      {
        label: "Path Finding Algorithms",
        links: [
          { label: "Dijkstra's Algorithm", to: "/robotics/dijkstras" },
          {
            label: "Dijkstra's Algorithm for Grids",
            to: "/robotics/dijkstras-for-grids",
          },
          {
            label: "A* Algorithm for Grids",
            to: "/robotics/astar-for-grids",
          },
        ],
      },
    ],
  },
  {
    value: "Math Tools",
    childrens: [
      {
        label: "Numerical Analysis",
        links: [
          {
            label:
              "Numerical Methods for\nOrdinary Differential Equations (ODE)",
            to: "/robotics/numerical-methods-for-ode",
          },
        ],
      },
    ],
  },
];

const tablist = [
  "Forward Kinematics",
  "Inverse Kinematics",
  "Dynamics",
  "Control Theory",
  "Motion Planning",
  "Math Tools",
];

const MegaMenu = () => {
  const location = useLocation();
  const { pathname } = location;
  const [value, setValue] = useState("Forward Kinematics");
  const [showMenu, setShowMenu] = useState(false);
  const [currentMenu, setCurrentMenu] = useState(menuDatas[0]);

  useEffect(() => {
    setCurrentMenu(menuDatas.filter((d) => d.value == value)[0]);
  }, [value]);

  const tabStyle = {
    fontSize: 15,
    minHeight: 24,
    height: 48,
    textTransform: "none",
    color: "#424242",
    "&:hover": {
      backgroundColor: "#eeeeee",
    },
  };

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
      <Tabs
        sx={{ minHeight: 24, height: 48 }}
        variant="scrollable"
        scrollButtons="auto"
        value={false}
      >
        {tablist.map((t, i) => {
          return (
            <Tab
              key={t}
              sx={{ ...tabStyle, marginLeft: i == 0 ? 4 : 0 }}
              label={t}
              onMouseOver={() => {
                setShowMenu(true);
                setValue(t);
              }}
              onMouseLeave={() => setShowMenu(false)}
            />
          );
        })}
      </Tabs>
      {showMenu && (
        <Card
          elevation={4}
          sx={{ width: "100%", position: "absolute", zIndex: 10 }}
          onMouseOver={() => setShowMenu(true)}
          onMouseLeave={() => setShowMenu(false)}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "row",
              width: "92%",
              marginRight: "auto",
              marginLeft: "auto",
            }}
          >
            {currentMenu.childrens.map((c) => (
              <Box
                key={c.label}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  marginRight: 10,
                }}
              >
                <Typography
                  sx={{ fontSize: 14, mb: 1 }}
                  color="text.secondary"
                  gutterBottom
                >
                  {c.label}
                </Typography>
                {c.links.map((sb) => (
                  <Link
                    key={sb.label}
                    sx={{
                      mb: 1,
                      fontSize: 18,
                      whiteSpace: "pre-line",
                      textAlign: "left",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                    underline="none"
                    component={RouterLink}
                    to={sb.to}
                  >
                    {sb.label}
                  </Link>
                ))}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MegaMenu;
